import type { Paragraph } from '@/api/types/stowed-element'

/**
 * **The policy this file is named for no longer exists.**
 * `allowBackwardPullAfterContentEdit` — "may this edit's reflow pull content back out of downstream
 * frames?" — was deleted in Phase C of the one-story refactor (`docs/chain-one-story.md`). D4 makes
 * every trigger re-split the whole story from the head, so a shortening edit reclaims and a
 * lengthening one cascades by the same code path; there is no longer a question to answer, and no
 * flag to answer it with. Reintroducing one would reintroduce the guess.
 *
 * What survives is the character counter that policy was built on, kept because
 * `delete-trailing-character.ts` needs it for something unrelated to chains.
 */

/**
 * Total characters held in `paragraphs`' runs.
 *
 * Deliberately a plain model walk rather than `paragraphsToPlainText` (`lib/shapes/shape-text.ts`),
 * which also counts the separator between paragraphs.
 *
 * Callers only ever compare two counts produced by THIS function, so the absolute value is
 * irrelevant — what matters is that the convention is identical on both sides. A raw run-character
 * count is the only one that is.
 *
 * **Not a story offset.** It counts no paragraph or line separators, so it cannot be used to index
 * into a story or to drive a split — `paragraphsToPlainText` from `lib/shapes/shape-text.ts` is the
 * convention `splitShapeTextParagraphsAtOffset` and `chain-story.ts` use for that.
 */
export function countRunCharacters(paragraphs: Paragraph[]): number {
  let total = 0

  for (const paragraph of paragraphs) {
    for (const line of paragraph.textLines ?? []) {
      for (const run of line.textRuns ?? []) {
        total += (run.textContent ?? '').length
      }
    }
  }

  return total
}
